/**
 * AdminBillingV2 - Invoice management with Holded integration.
 * Design from Lovable, logic from chirigota.
 */
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
	FileText,
	Euro,
	AlertCircle,
	CalendarClock,
	CreditCard,
	Pencil,
	Download,
	Building2,
	Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { PageHeader } from '../../../components/ui/page-header';
import { Badge } from '../../../components/ui/badge';
import ResponsiveTooltip from '../../../components/ui/responsive-tooltip';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow
} from '../../../components/ui/table';
import { ArcaContext } from '../../../context/ArcaContext.context';
import { I18nContext } from '../../../context/translations/TranslationsContext.context';
import getHoldedInvoices from '../../../api/invoices/getHoldedInvoices';
import getCompanyInfoEppConfig from '../../../api/companies/getCompanyInfoEppConfig';
import { getHoldedInvoicePdf } from '../../../api/invoices/getHoldedInvoicePdf';
import { HoldedInvoice } from '../../../api/invoices/HoldedInvoice';
import { HoldedInvoiceStatus } from '../../../types/enums';
import { CompanyWithEppConfigI } from '../../../api/companies/interfaces/CompanyInfo';
import { useToast } from '../../../components/ui/use-toast';
import { descargarFichero } from '../../../movil/descargas';

interface HoldedDoc {
	id: string;
	docNumber: string;
	date: string;
	timestamp: number;
	concept: string;
	amount: number;
	status: HoldedInvoiceStatus;
	contactName: string;
	companyId: string;
}

const fadeUp = (delay = 0) => ({
	initial: { opacity: 0, y: 12 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.35, delay }
});

const AdminBillingV2: React.FC = () => {
	const {
		state: { translate }
	} = useContext(I18nContext);
	const { token, companyId } = useContext(ArcaContext);

	const [isLoading, setIsLoading] = useState(true);
	const [holdedDocs, setHoldedDocs] = useState<HoldedDoc[]>([]);
	const [allCompanies, setAllCompanies] = useState<CompanyWithEppConfigI[]>([]);
	const [downloadingId, setDownloadingId] = useState<string | null>(null);
	const { toast } = useToast();

	// Company IBANs - in production, fetch from backend
	const [companyIbans, setCompanyIbans] = useState<Record<string, { iban: string; bank: string }>>(
		{}
	);

	const formatHoldedDate = (ts: number | undefined): string => {
		if (!ts) return '';
		const date = new Date(ts * 1000);
		const pad = (n: number) => n.toString().padStart(2, '0');
		return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
	};

	const formatCurrency = (amount: number): string => {
		return new Intl.NumberFormat('es-ES', {
			style: 'currency',
			currency: 'EUR',
			minimumFractionDigits: 2
		}).format(amount);
	};

	useEffect(() => {
		const fetchDocuments = async () => {
			if (!token || !companyId) return;

			try {
				const [companyConfigResponse, holdedResponse] = await Promise.all([
					getCompanyInfoEppConfig(token, companyId),
					getHoldedInvoices(token, companyId)
				]);

				// Process company config
				const companies = companyConfigResponse.data?.allCompanies || [];
				setAllCompanies(companies);

				// Initialize company IBANs from company data
				const ibans: Record<string, { iban: string; bank: string }> = {};
				companies.forEach((c) => {
					ibans[c.id] = {
						iban: c.bankAccount || c.saasBankAccount || '',
						bank: ''
					};
				});
				setCompanyIbans(ibans);

				// Process Holded invoices
				if (holdedResponse.success) {
					const fetchedHoldedDocs = holdedResponse.data.documents
						.filter(
							(doc: HoldedInvoice & { companyId?: string }) =>
								doc.status !== HoldedInvoiceStatus.CANCELLED
						)
						.map((doc: HoldedInvoice & { companyId?: string }) => ({
							id: doc.id,
							docNumber: doc.docNumber || doc.id,
							date: formatHoldedDate(doc.date),
							timestamp: doc.date,
							concept: doc.desc || 'Arca Fee',
							amount: Number(doc.total || doc.subtotal || 0),
							status: doc.status,
							contactName: doc.contactName,
							companyId: doc.companyId || ''
						}))
						.sort((a: HoldedDoc, b: HoldedDoc) => b.timestamp - a.timestamp);
					setHoldedDocs(fetchedHoldedDocs);
				}
			} catch (error) {
				console.error('Error fetching invoices:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchDocuments();
	}, [token, companyId]);

	const handleDownloadInvoice = async (doc: HoldedDoc) => {
		if (!token) return;
		setDownloadingId(doc.id);
		try {
			const response = await getHoldedInvoicePdf(token, 'invoice', doc.id);
			if (response.success && response.data?.pdfContent) {
				const fileName = doc.contactName
					? `${doc.docNumber}_${doc.contactName}_${doc.date}.pdf`
					: `${doc.docNumber}.pdf`;
				await descargarFichero({
					nombre: fileName.replace(/\//g, '-'),
					base64: response.data.pdfContent,
					tipo: 'application/pdf'
				});
			} else {
				toast({
					variant: 'destructive',
					title: translate('admin.billing.downloadError'),
					description: translate('admin.billing.downloadErrorDescription')
				});
			}
		} catch (error) {
			console.error('Error downloading invoice:', error);
			toast({
				variant: 'destructive',
				title: translate('admin.billing.downloadError'),
				description: translate('admin.billing.downloadErrorDescription')
			});
		} finally {
			setDownloadingId(null);
		}
	};

	// Calculate KPIs
	const totalInvoiced = useMemo(() => {
		return holdedDocs.reduce((sum, doc) => sum + doc.amount, 0);
	}, [holdedDocs]);

	const pendingCount = useMemo(() => {
		return holdedDocs.filter((doc) => doc.status === HoldedInvoiceStatus.PENDING).length;
	}, [holdedDocs]);

	const nextInvoice = useMemo(() => {
		const pending = holdedDocs.filter((doc) => doc.status === HoldedInvoiceStatus.PENDING);
		if (pending.length === 0) return null;
		return pending.sort((a, b) => a.timestamp - b.timestamp)[0];
	}, [holdedDocs]);

	const isHolding = allCompanies.length > 1;

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	const formatIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim();

	const maskIban = (iban: string) => {
		if (!iban || iban.length < 10) return iban;
		return `•••• •••• •••• •••• •••• ${iban.slice(-4)}`;
	};

	return (
		<div className="max-w-6xl mx-auto space-y-8">
			{/* Cabecera — la pone `ui/PageHeader`, puerto de Proa v0.10.0. Antes el `h1` iba a
			    `text-xl font-bold`: era el título más pequeño de las seis pantallas de admin, y sin
			    `font-heading`.
			    `width="full"` porque el ancho ya lo pone el contenedor de esta pantalla
			    (`max-w-6xl`, justo arriba); ponerlo también aquí sería repetirlo. Quitar ese
			    contenedor a mano es el paso siguiente, no este. */}
			<motion.div {...fadeUp()}>
				<PageHeader
					width="full"
					title={translate('admin.billing.title')}
					subtitle={
						<>
							{translate('admin.billing.subtitle')}{' '}
							{isHolding
								? `${allCompanies.length} ${translate('admin.billing.companiesInGroup')}`
								: ''}
						</>
					}
				/>
			</motion.div>

			{/* KPI Cards */}
			<motion.div {...fadeUp(0.06)}>
				<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-2">
								<Euro className="w-4 h-4 text-muted-foreground" />
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									{translate('admin.billing.totalInvoiced')}
								</p>
							</div>
							<p className="text-2xl font-bold text-foreground">{formatCurrency(totalInvoiced)}</p>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-2">
								<AlertCircle className="w-4 h-4 text-muted-foreground" />
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									{translate('admin.billing.pendingInvoices')}
								</p>
							</div>
							<p className="text-2xl font-bold text-foreground">{pendingCount}</p>
						</CardContent>
					</Card>
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex items-center gap-2 mb-2">
								<CalendarClock className="w-4 h-4 text-muted-foreground" />
								<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
									{translate('admin.billing.nextInvoice')}
								</p>
							</div>
							<p className="text-2xl font-bold text-foreground">
								{nextInvoice ? formatCurrency(nextInvoice.amount) : '—'}
							</p>
							{nextInvoice && (
								<p className="text-xs text-muted-foreground mt-1">{nextInvoice.date}</p>
							)}
						</CardContent>
					</Card>
				</div>
			</motion.div>

			{/* Bank Accounts Section */}
			<motion.div {...fadeUp(0.1)}>
				<div className="flex items-center gap-2 mb-4">
					<CreditCard className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">
						{isHolding
							? translate('admin.billing.bankAccountsPlural')
							: translate('admin.billing.bankAccount')}
					</h2>
				</div>

				{isHolding ? (
					<div className="space-y-3">
						{allCompanies.map((gc) => {
							const acct = companyIbans[gc.id] || { iban: '', bank: '' };
							const masked = maskIban(acct.iban);
							return (
								<Card key={gc.id} className="border-none shadow-xs">
									<CardContent className="p-4">
										<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
											<div className="flex items-center gap-3">
												<div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
													<span className="text-sm font-bold text-primary">
														{gc.name.charAt(0)}
													</span>
												</div>
												<div>
													<p className="text-sm font-semibold text-foreground">{gc.name}</p>
													<p className="text-xs text-muted-foreground font-mono tracking-wide">
														{masked || translate('admin.billing.noIbanConfigured')}
													</p>
													{acct.bank && (
														<p className="text-[11px] text-muted-foreground">{acct.bank}</p>
													)}
												</div>
											</div>
											<ResponsiveTooltip content={translate('admin.billing.comingSoon')} side="top">
												<span>
													<Button
														variant="outline"
														size="sm"
														className="gap-1.5 self-start"
														disabled
													>
														<Pencil className="w-3.5 h-3.5" />
														{translate('admin.billing.change')}
													</Button>
												</span>
											</ResponsiveTooltip>
										</div>
									</CardContent>
								</Card>
							);
						})}
					</div>
				) : (
					<Card className="border-none shadow-xs">
						<CardContent className="p-5">
							<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
								<div className="flex items-center gap-4">
									<div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
										<Building2 className="w-6 h-6 text-primary" />
									</div>
									<div>
										<p className="text-sm font-semibold text-foreground">
											{companyIbans[companyId]?.bank ||
												translate('admin.billing.bankNotConfigured')}
										</p>
										<p className="text-sm text-muted-foreground font-mono tracking-wide">
											{maskIban(companyIbans[companyId]?.iban || '') ||
												translate('admin.billing.noIbanConfigured')}
										</p>
										<p className="text-xs text-muted-foreground mt-0.5">
											{translate('admin.billing.ibanDescription')}
										</p>
									</div>
								</div>

								<ResponsiveTooltip content={translate('admin.billing.comingSoon')} side="top">
									<span>
										<Button variant="outline" size="sm" className="gap-1.5 self-start" disabled>
											<Pencil className="w-3.5 h-3.5" />
											{translate('admin.billing.changeAccount')}
										</Button>
									</span>
								</ResponsiveTooltip>
							</div>
						</CardContent>
					</Card>
				)}
			</motion.div>

			{/* Invoice History Table */}
			<motion.div {...fadeUp(0.16)}>
				<div className="flex items-center gap-2 mb-4">
					<FileText className="w-5 h-5 text-primary" />
					<h2 className="text-lg font-semibold">{translate('admin.billing.invoiceHistory')}</h2>
				</div>

				{holdedDocs.length > 0 ? (
					<Card className="border-none shadow-xs overflow-hidden">
						<Table>
							<TableHeader className="bg-muted/50">
								<TableRow>
									<TableHead>{translate('admin.billing.invoiceNumber')}</TableHead>
									<TableHead>{translate('admin.billing.date')}</TableHead>
									<TableHead>{translate('admin.billing.concept')}</TableHead>
									<TableHead className="text-right">{translate('admin.billing.amount')}</TableHead>
									<TableHead>{translate('admin.billing.status')}</TableHead>
									<TableHead className="text-center">
										{translate('admin.billing.actions')}
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{holdedDocs.map((doc) => (
									<TableRow key={doc.id} className="hover:bg-muted/30">
										<TableCell className="font-medium text-foreground">{doc.docNumber}</TableCell>
										<TableCell className="text-muted-foreground">{doc.date}</TableCell>
										<TableCell>{doc.concept}</TableCell>
										<TableCell className="text-right font-medium">
											{formatCurrency(doc.amount)}
										</TableCell>
										<TableCell>
											{doc.status === HoldedInvoiceStatus.PAID ? (
												<Badge className="bg-primary/10 text-primary border-0 text-[10px] uppercase tracking-wider">
													✓ {translate('admin.billing.paid')}
												</Badge>
											) : (
												<Badge
													variant="outline"
													className="text-[10px] uppercase tracking-wider border-amber-500 text-amber-600"
												>
													{translate('admin.billing.pending')}
												</Badge>
											)}
										</TableCell>
										<TableCell className="text-center">
											<Button
												variant="ghost"
												size="sm"
												className="text-primary hover:bg-primary/10 gap-1"
												onClick={() => handleDownloadInvoice(doc)}
												disabled={downloadingId === doc.id}
											>
												{downloadingId === doc.id ? (
													<Loader2 className="w-4 h-4 animate-spin" />
												) : (
													<Download className="w-4 h-4" />
												)}
												<span className="hidden sm:inline">PDF</span>
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</Card>
				) : (
					<Card className="border-none shadow-xs">
						<CardContent className="p-8 text-center">
							<FileText className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
							<p className="text-muted-foreground">{translate('admin.billing.noInvoices')}</p>
						</CardContent>
					</Card>
				)}
			</motion.div>
		</div>
	);
};

export default AdminBillingV2;
